import {convert, Duration, LocalDate, LocalTime, nativeJs} from '@js-joda/core';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import {usePreferencesState} from '@tracker/shared/src//Preferences/PreferencesProvider';
import {useMsal} from '@tracker/shared/src/Auth/Msal';
import {Decorator, FormApi} from 'final-form';
import {makeRequired, makeValidate} from 'mui-rff';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Form} from 'react-final-form';
import * as Yup from 'yup';
import {Task, TimeEntry} from '../../pages/TimeEntries';
import {MyComponent, MyProject, useProjects} from "../Tasks/MyProjectsProvider";
import TimeEntryForm from "./TimeEntryForm";

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
}));

type Props = {
    open : boolean;
    timeEntry ?: Partial<TimeEntry>;
    onClose : () => void;
    onSubmit : (timeEntry : TimeEntry) => void;
};

export type FormData = {
    note : string;
    task : Task | null;
    project : MyProject | null;
    component : MyComponent | null;
    startDate : Date;
    startTime : Date | null;
    endTime : Date | null;
    hours : number | null;
    minutes : number | null;
    isPMTimeEntry : boolean;
};

const schema = Yup.object().shape({
    startDate: Yup.date().label('Start date').required().typeError('Invalid date'),
    startTime: Yup.date().nullable().label('Start time').typeError('Invalid time'),
    endTime: Yup.date().nullable().label('End time').typeError('Invalid time'),
    hours: Yup.number().nullable().label('Hours').min(0).typeError('Invalid number'),
    minutes: Yup.number().nullable().label('Minutes').min(0).max(59).typeError('Invalid number'),
    note: Yup.string().label('Note').ensure().trim(),
    task: Yup.object().nullable().label('Task'),
    project: Yup.object().nullable().label('Project'),
    component: Yup.object().nullable().label('Component'),
    isPMTimeEntry: Yup.boolean().nullable().label('PM Time'),
}).required();

const validate = makeValidate(schema);
export const required = makeRequired(schema);

const updateMinutes = (form : FormApi<FormData>, values : FormData, previousValues ?: FormData) => {
    if (!previousValues
        || (!values.startDate || !values.startTime)
        || (isNaN(values.startDate.valueOf()) || isNaN(values.startTime.valueOf()))
        || (
            previousValues.startDate === values.startDate
            && previousValues.startTime === values.startTime
            && previousValues.endTime === values.endTime
        )
    ) {
        return {};
    }

    if (!values.endTime || isNaN(values.endTime.valueOf())) {
        return {
            hours: null,
            minutes: null,
        };
    }

    const startDate = LocalDate.from(nativeJs(values.startDate));
    const startTime = LocalTime.from(nativeJs(values.startTime)).withSecond(0).withNano(0);
    const endTime = LocalTime.from(nativeJs(values.endTime)).withSecond(0).withNano(0);
    const startDateTime = startDate.atTime(startTime);
    let endDateTime;

    if (endTime >= startTime) {
        endDateTime = startDate.atTime(endTime);
    } else {
        endDateTime = startDate.plusDays(1).atTime(endTime);
    }

    const totalMinutes = Duration.between(startDateTime, endDateTime).toMinutes();

    return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
    };
};

const updateEndTime = (form : FormApi<FormData>, values : FormData, previousValues ?: FormData) => {
    if (!previousValues
        || (!values.startDate || !values.startTime)
        || (isNaN(values.startDate.valueOf()) || isNaN(values.startTime.valueOf()))
        || (
            previousValues.hours === values.hours
            && previousValues.minutes === values.minutes
        )
    ) {
        return {};
    }

    if (values.hours === null && values.minutes === null) {
        return {endTime: null};
    }

    const hours = transformNumber(values.hours);
    const minutes = transformNumber(values.minutes);

    if (isNaN(hours) || isNaN(minutes)) {
        // Conditionally catch invalid numbers (validation didn't run yet).
        return {};
    }

    if (hours < 0 || minutes < 0 || minutes > 59) {
        return {};
    }

    if (hours > 23) {
        return {endTime: null};
    }

    const startDate = LocalDate.from(nativeJs(values.startDate));
    const startTime = LocalTime.from(nativeJs(values.startTime)).withSecond(0).withNano(0);
    const startDateTime = startDate.atTime(startTime);
    const endDateTime = startDateTime.plusMinutes(hours * 60 + minutes);

    return {endTime: convert(endDateTime).toDate()};
};

export const transformNumber = (input : string | number | null) : number => {
    if (input === null) {
        return 0;
    }

    let parsed = input;

    if (typeof parsed === 'string') {
        parsed = parsed.replace(/\s/g, '');

        if (parsed === '') {
            return NaN;
        }

        parsed = +parsed;
    }

    return parsed;
};

const decorator : Decorator<FormData> = form => {
    let previousValues : FormData;

    return form.subscribe(({values}) => {
        form.batch(() => {
            const updates = {
                ...updateMinutes(form, values, previousValues),
                ...updateEndTime(form, values, previousValues),
            };

            for (const [key, value] of Object.entries(updates)) {
                form.change(key as keyof FormData, value);
            }

            previousValues = {...values, ...updates};
        });
    }, {values: true});
};

const TimeEntryFormDialog : React.FC<Props> = ({open, timeEntry, onClose, onSubmit} : Props) =>{
    const classes = useStyles();
    const [enterDuration, setEnterDuration] = useState(true);
    const {preferences} = usePreferencesState();
    const {myProjects} = useProjects();
    const msal = useMsal();

    const initialValues = useMemo(() => {
        const values : Partial<FormData> = {};

        if (!timeEntry) {
            return values;
        }

        if (timeEntry.note) {
            values.note = timeEntry.note;
        }

        if (timeEntry.task) {
            values.task = timeEntry.task;
        }

        if (timeEntry.project) {
            values.project = timeEntry.project;
        }

        if (timeEntry.component) {
            values.component = timeEntry.component;
        }

        if (timeEntry.isPMTimeEntry) {
            values.isPMTimeEntry = timeEntry.isPMTimeEntry === 1;
        }

        if (timeEntry.startDate) {
            values.startDate = convert(timeEntry.startDate).toDate();

            if (timeEntry.startTime) {
                values.startTime = convert(
                    timeEntry.startDate.atTime(timeEntry.startTime)
                ).toDate();
            }
        }

        if (typeof timeEntry.minutes === 'number') {
            values.hours = Math.floor(timeEntry.minutes / 60);
            values.minutes = timeEntry.minutes % 60;

            if (timeEntry.startDate && timeEntry.startTime && values.hours < 23) {
                const endDateTime = timeEntry.startDate.atTime(timeEntry.startTime).plusMinutes(timeEntry.minutes);
                values.endTime = convert(endDateTime).toDate();
            }
        } else if (timeEntry.startDate && timeEntry.endTime) {
            values.endTime = convert(timeEntry.startDate.atTime(timeEntry.endTime)).toDate();
        }

        return values;
    }, [timeEntry]);

    useEffect(() => {
        if (!initialValues.endTime && initialValues.hours) {
            return setEnterDuration(true);
        }

        setEnterDuration(preferences.timeEntry.defaultTimeInput === 'duration');
    }, [initialValues, preferences.timeEntry.defaultTimeInput]);

    // make sure we don't need a interactive login when they open a time entry.
    //  when you create a new time entry record we don't make a new dialog, so we need to also depend on open.
    useEffect(() => {
        if (open) {
            msal.getAccessToken().catch((e) => {
                console.error('error fetching access token', e);//this should never happen as it should redirect the user.
            })
        }
    }, [msal, open]);


    const handleSubmit = useCallback((values : FormData) => {
        const data = schema.cast(values);

        let startTime = null;
        let minutes : number | null = null;
        let endTime = null;

        if (data.startTime) {
            startTime = LocalTime.from(nativeJs(data.startTime)).withSecond(0);
        }

        if (!enterDuration && data.endTime) {
            endTime = LocalTime.from(nativeJs(data.endTime)).withSecond(0);
        }

        if (enterDuration) {
            if (data.hours || data.hours === 0) {
                minutes = data.hours * 60;
            }

            if (data.minutes || data.minutes === 0) {
                minutes = (minutes ? minutes : 0) + data.minutes;
            }
        }

        const project = data.task ? undefined : data.project as (MyProject | undefined) || undefined;//if we have a task ignore project and component
        const dataComponent = project ? data.component as (MyComponent | undefined) || undefined : undefined;
        const component = dataComponent && project ? myProjects.find((p) => p.id === project.id)
            ?.components.find((c) => c.id === dataComponent.id) : undefined;//make sure the component is part of the project

        const newTimeEntry : TimeEntry = {
            id: (timeEntry && timeEntry.id) ? timeEntry.id : 0,
            note: data.note || '',
            task: data.task as (Task | null) || null,
            component,
            project,
            startDate: LocalDate.from(nativeJs(data.startDate)),
            startTime,
            minutes,
            endTime,
            submitted: (timeEntry && timeEntry.submitted) ? timeEntry.submitted : false,
            invoiced: (timeEntry && timeEntry.invoiced) ? timeEntry.invoiced : false,
            error: timeEntry?.error ?? '',
            excludeFromBilling: (timeEntry && timeEntry.excludeFromBilling !== undefined)
                ? timeEntry.excludeFromBilling : false,
            billableMinutesOverride : (timeEntry && timeEntry.billableMinutesOverride !== undefined)
                ? timeEntry.billableMinutesOverride : null,
            isPMTimeEntry: data.isPMTimeEntry ? 1 : 0,
        };

        onSubmit(newTimeEntry);
    }, [enterDuration, myProjects, timeEntry, onSubmit]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle disableTypography>
                <Typography variant="h6">{timeEntry && timeEntry.id ? 'Edit' : 'Create'} Time Entry</Typography>
                <IconButton className={classes.closeButton} onClick={onClose}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <Form
                subscription={{submitting: true, pristine: true}}
                decorators={[decorator]}
                onSubmit={handleSubmit}
                initialValues={initialValues}
                validate={validate}
                render={({form, handleSubmit, modifiedSinceLastSubmit, submitting}) => (
                    <form onSubmit={handleSubmit} noValidate>
                        <TimeEntryForm
                            initialValues={initialValues}
                            form={form}
                            modifiedSinceLastSubmit={modifiedSinceLastSubmit}
                            submitting={submitting}
                            enterDuration={enterDuration}
                            setEnterDuration={setEnterDuration}
                        />
                    </form>
                )}
            />
        </Dialog>
    );
};

export default TimeEntryFormDialog;
